import React, { useState, useEffect, useMemo } from 'react';
import { Card } from 'pokemon-tcg-sdk-typescript';
import { useCollectionStore } from '../../lib/collectionStore';
import CardGrid from '../../components/CardGrid';
import { useNavigate } from 'react-router-dom';
import { slugify } from '../../utils/slugify';
import { supabase } from '../../lib/supabase';


export default function UniqueCardsView() {
  const { collection } = useCollectionStore();
  const navigate = useNavigate();

  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchCards = async () => {
      try {
        if (!collection.length) {
          if (isMounted) {
            setCards([]);
            setLoading(false);
          }
          return;
        }

        if (isMounted) {
          setLoading(true);
          setError(null);
        }

        // Get unique card IDs
        const uniqueCardIds = Array.from(new Set(collection.map(item => item.cardId)));

        // Fetch cards from Supabase in batches to avoid query limits
        let allCardData = [];
        const batchSize = 1000;
        
        for (let i = 0; i < uniqueCardIds.length; i += batchSize) {
          const batchIds = uniqueCardIds.slice(i, i + batchSize);
          
          const { data: batchData, error: batchError } = await supabase
            .from('pokemontcg_io')
            .select(`
              *,
              set:pokemontcg_io_sets(*)
            `)
            .in('id', batchIds)
            .abortSignal(abortController.signal);
            
          if (batchError) {
            throw batchError;
          }
          
          if (batchData) {
            allCardData.push(...batchData);
          }
        }
        
        const cardData = allCardData;
        const cardError = null;

        if (!isMounted) return;

        if (cardError) {
          throw cardError;
        }

        if (!cardData) {
          throw new Error('No card data returned');
        }

        // Transform the cards
        const transformedCards = cardData.map(card => ({
          ...card,
          images: {
            small: card.image_small_url,
            large: card.image_large_url
          },
          set: {
            ...card.set,
            releaseDate: card.set.release_date,
            images: {
              symbol: card.set.symbol_url,
              logo: card.set.logo_url
            }
          }
        }));

        if (isMounted) {
          setCards(transformedCards);
          setLoading(false);
          setError(null);
        }
      } catch (error) {
        if (!isMounted) return;

        console.error('Error fetching cards:', error);
        setError('Error loading cards. Please try again.');
        
        if (retryCount < MAX_RETRIES) {
          setTimeout(() => {
            if (isMounted) {
              setRetryCount(prev => prev + 1);
            }
          }, RETRY_DELAY);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCards();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [collection, retryCount]);

  const handleCardClick = (card: Card) => {
    const seriesSlug = slugify(card.set.series);
    const setSlug = slugify(card.set.name);
    const cardSlug = `${slugify(card.name)}-${card.number}`;
    navigate(`/${seriesSlug}/${setSlug}/${cardSlug}`);
  };



  return (
    <div className="space-y-6">
      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold mb-2">Unique Cards</h2>
            <p className="text-white/60">
              Showing one of each unique card in your collection.
            </p>
          </div>
          {cards.length > 0 && (
            <div className="text-sm text-white/60">
              {cards.length} unique cards total
            </div>
          )}
        </div>

      </div>

      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
        </div>
              ) : cards.length === 0 ? (
        <div className="text-center py-12 text-white/60">
          No cards in your collection yet.
        </div>
      ) : (
        <CardGrid
          key={`unique-cards-${cards.length}`}
          cards={cards}
          onCardClick={handleCardClick}
          loading={loading}
          isCollectionView={true}
          enablePagination={true}
          cardsPerPage={250}
        />
      )}
    </div>
  );
}